import { Button, Stack, Tooltip } from '@mui/material'
import { DataStore } from 'aws-amplify'
import BigNumber from 'bignumber.js'
import { useCurrentDeckContext } from 'components/currentDeckContext/CurrentDeckContext'
import { DeckMappDialog } from 'components/DeckMappDialog'
import { DeckMappToolTip } from 'components/tooltip/DeckMappToolTip'
import { updateDeck } from 'data/deckOperations'
import { createFirstRevision, createRevision, updateRevision } from 'data/revisionOperations'
import { useCurrentUser } from 'hooks'
import { DeckRevisions, KitsItems, KitSizeItems } from 'models'
import { useSnackbar } from 'notistack'
import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useCSVDownloader, useCSVReader } from 'react-papaparse'
import { useLocation, useNavigate } from 'react-router-dom'
import { DescriptionModal } from './v2/DescriptionModal'
import { parsedRevisionNumberWithSubLetter } from 'common/revisionNumber'
import { LoadingButton } from '@mui/lab'

const buttonStyling = {
  margin: '0 0.5em 0 0.5em'
}

const csvConfig = {
  header: true,
  skipEmptyLines: true
}

const createButton = (text, onClick, disabled, tooltip) => {
  return (
    <Tooltip title={tooltip}>
      <span>
        <Button variant="outlined" color="secondary" onClick={onClick} sx={buttonStyling} disabled={disabled}>
          {text}
        </Button>
      </span>
    </Tooltip>
  )
}

export const CreateRevision = ({ deckRevision }) => {
  const { setDeckRevisionId, setDeckRevision, history, deck, database } = useCurrentDeckContext()

  const { CSVReader } = useCSVReader()
  const { CSVDownloader } = useCSVDownloader()
  const { enqueueSnackbar } = useSnackbar()
  const [dialogData, setDialogData] = useState({
    isOpen: false,
    title: '',
    type: '',
    onSave: undefined
  })
  const { state = {} } = useLocation()

  const currentMaxRevision = useMemo(() => {
    return database.deckRevisions
      .filter((d) => d.DeckId == deck.id)
      .sort((a, b) => b.RevisionNumber - a.RevisionNumber)[0]
  }, [database.deckRevisions, deck])

  const currentMaxRevisionTrial = useMemo(() => {
    if (!currentMaxRevision) {
      return undefined
    }

    return database.deckRevisions
      .filter((d) => d.DeckId === deck.id && d.RevisionNumber === currentMaxRevision?.RevisionNumber && d.IsTrial)
      .sort((a, b) => b.MinorRevisionNumber - a.MinorRevisionNumber)
      .sort((a, b) => b.RevisionLevel - a.RevisionLevel)[0]
  }, [currentMaxRevision, database.deckRevisions, deck.id])

  const [isLoading, setIsLoading] = useState(false)

  const navigate = useNavigate()

  const descriptionRef = useRef({
    description: ''
  })

  useEffect(() => {
    setIsLoading(!dialogData.isOpen)
  }, [dialogData.isOpen])

  const kitsToExport = useMemo(() => {
    if (!deckRevision) {
      return []
    }
    return deckRevision.Kits?.map((k) => {
      const isOther =
        !deckRevision.Panels?.some((p) => p.MaterialNumber === k.MaterialNumber) &&
        !deckRevision.SideLiners?.some((s) => s.MaterialNumber === k.MaterialNumber)
      return {
        Quantity: isOther
          ? k.Quantity
          : new BigNumber(deckRevision.Panels?.filter((p) => p.MaterialNumber === k.MaterialNumber).length)
              .plus(deckRevision.SideLiners?.filter((s) => s.MaterialNumber === k.MaterialNumber).length)
              .toNumber(),
        SandvikMaterialNumber: k.MaterialDescription ? k.MaterialNumber : '',
        Description: k.MaterialDescription ? k.MaterialDescription : k.MaterialNumber,
        PartNumber: k.PartNumber,
        Code: k.CustomerStockCode,
        Weight: k.Weight,
        Price: k.UnitPrice,
        OpenArea: k.OpenArea,
        WorkingDepth: k.WorkingDepth,
        FailureWorkingDepth: k.FailureWorkingDepth,
        ApertureWidth: k.ApertureWidth,
        FailureApertureWidth: k.FailureApertureWidth,
        MaterialType: k.MaterialType,
        Colour: k.Colour,
        Height: k.Size?.Height ?? 1,
        Width: k.Size?.Width ?? 1
      }
    })
  }, [deckRevision])

  const { getUserName, getTransactionMetaData } = useCurrentUser()

  const onNewRevision = async () => {
    setIsLoading(true)
    const newRevision = await createFirstRevision(deck, getTransactionMetaData())
    setDeckRevision(structuredClone(newRevision))
    setDeckRevisionId(newRevision.id)
    setIsLoading(false)
  }

  const importKits = (results) => {
    results.data.forEach((result) => {
      if (!deckRevision.Kits.find((k) => k.MaterialNumber === result.SandvikMaterialNumber)) {
        const description = result.MaterialDescription ?? result.Description
        deckRevision.Kits.push(
          new KitsItems({
            Quantity: Number(result.Quantity ?? 0),
            PartNumber: result.PartNumber,
            CustomerStockCode: result.CustomerStockCode ?? result.Code,
            OpenArea: result.OpenArea,
            MaterialNumber: result.SandvikMaterialNumber ? result.SandvikMaterialNumber : description,
            MaterialType: Number(result.MaterialType),
            MaterialDescription: result.SandvikMaterialNumber ? description : '',
            Colour: result.Colour,
            Weight: result.Weight,
            WorkingDepth: result.WorkingDepth,
            FailureWorkingDepth: result.FailureWorkingDepth,
            ApertureWidth: result.ApertureWidth,
            FailureApertureWidth: result.FailureApertureWidth,
            Size: new KitSizeItems({
              Height: Number(result.Height ?? 1),
              Width: Number(result.Width ?? 1)
            })
          })
        )
      }
    })
  }
  const hasDeck = deck && Object.entries(deck).length
  async function updateDeckIfSizeChange(deckRevision) {
    if (
      deckRevision.Size.Columns !== deck.DeckHeader.DeckSize.Columns ||
      deckRevision.Size.Rows !== deck.DeckHeader.DeckSize.Rows
    ) {
      const clone = structuredClone(deck)
      clone.DeckHeader.DeckSize.Columns = deckRevision.Size.Columns
      clone.DeckHeader.DeckSize.Rows = deckRevision.Size.Rows
      await updateDeck(clone, deck.id, getTransactionMetaData())
    }
  }

  const onTrial = useCallback(
    async (isUpdate) => {
      const description = descriptionRef.current.description
      setIsLoading(true)
      enqueueSnackbar(
        !isUpdate ? (deckRevision.IsProposal ? 'Creating trial from proposal' : 'Creating trial') : 'Updating trial'
      )
      try {
        const original = await DataStore.query(DeckRevisions, deckRevision.id)
        if (original?.IsTrial) {
          await updateRevision(
            original,
            {
              ...structuredClone(original),
              IsArchived: true,
              Type: 'Reworked Trial'
            },
            getTransactionMetaData()
          )
        }

        if (original?.IsProposal) {
          await updateRevision(
            original,
            {
              ...structuredClone(original),
              Type: 'Proposal Trialed'
            },
            getTransactionMetaData()
          )
        }
        deckRevision.IsPublished = false
        deckRevision.IsArchived = false
        deckRevision.Description = description
        deckRevision.CustomerId = deck.CustomerId
        deckRevision.SiteId = deck.SiteId
        deckRevision.IsProposal = false
        deckRevision.IsTrial = true
        deckRevision.Type = 'Trial'
        deckRevision.Status = null
        if (isUpdate) {
          deckRevision.RevisionLevel = (deckRevision.RevisionLevel ?? 0) + 1
        } else {
          const deckRevisions = await DataStore.query(DeckRevisions)
          if (original?.IsProposal) {
            const maxRevision = deckRevisions
              .filter((d) => d.DeckId === deck.id)
              .sort((a, b) => {
                return b.RevisionNumber - a.RevisionNumber
              })[0]
            const currentMax = deckRevisions
              .filter((d) => d.DeckId === deck.id && d.RevisionNumber === maxRevision.RevisionNumber && d.IsTrial)
              .sort((a, b) => b.MinorRevisionNumber - a.MinorRevisionNumber)[0]

            deckRevision.RevisionLevel = 0
            deckRevision.MinorRevisionNumber = (currentMax?.MinorRevisionNumber ?? 0) + 1
            deckRevision.RevisionNumber = maxRevision?.RevisionNumber
          } else if (state?.isEditingOld) {
            deckRevision.RevisionNumber = Math.max(
              ...deckRevisions.filter((d) => d.DeckId === deck.id).map((d) => d.RevisionNumber)
            )

            const maxRevision = deckRevisions
              .filter((d) => d.DeckId === deck.id)
              .sort((a, b) => {
                return b.RevisionNumber - a.RevisionNumber
              })[0]

            const currentMax = deckRevisions
              .filter((d) => d.DeckId === deck.id && d.RevisionNumber === maxRevision.RevisionNumber)
              .sort((a, b) => b.MinorRevisionNumber - a.MinorRevisionNumber)[0]

            deckRevision.RevisionLevel = 0
            deckRevision.MinorRevisionNumber = (currentMax?.MinorRevisionNumber ?? 0) + 1
            deckRevision.RevisionNumber = maxRevision?.RevisionNumber
          } else {
            deckRevision.RevisionLevel = 0
            const lastTrials = deckRevisions
              .filter((deckRevision_) => {
                return (
                  deckRevision_.DeckId === deck.id &&
                  deckRevision_.RevisionNumber === deckRevision.RevisionNumber &&
                  deckRevision_.IsTrial &&
                  deckRevision_.IsArchived
                )
              })
              .sort((a, b) => {
                return b.MinorRevisionNumber - a.MinorRevisionNumber
              })
            deckRevision.MinorRevisionNumber = (lastTrials[0]?.MinorRevisionNumber ?? 0) + 1
          }
        }

        const newRevision = await createRevision(deckRevision, getTransactionMetaData())

        setDeckRevision(structuredClone(newRevision))
        setDeckRevisionId(newRevision.id)
        enqueueSnackbar(isUpdate ? 'Trial successfully updated' : 'Trial successfully created')
      } catch (e) {
        enqueueSnackbar(`An error occurred while ${isUpdate ? 'updating' : 'creating'} trial`, {
          variant: 'error'
        })
      }
      setDialogData({
        isOpen: false
      })
    },
    [deckRevision, deck, state?.isEditingOld]
  )

  const onProposal = useCallback(
    async (isUpdate = false) => {
      setIsLoading(true)
      const description = descriptionRef.current.description
      enqueueSnackbar(!isUpdate ? 'Creating proposal' : 'Updating proposal')

      deckRevision.IsPublished = false
      deckRevision.Description = description
      deckRevision.CustomerId = deck.CustomerId
      deckRevision.SiteId = deck.SiteId
      deckRevision.IsProposal = true
      deckRevision.IsArchived = false
      deckRevision.CreatedBy = getUserName()
      if (isUpdate) {
        const existing = await DataStore.query(DeckRevisions, deckRevision.id)
        const updatedRevision = await updateRevision(existing, deckRevision, getTransactionMetaData())
        setDeckRevision(structuredClone(updatedRevision))
        setDeckRevisionId(updatedRevision.id)
        enqueueSnackbar('Proposal updated')
      } else {
        const deckRevisions = await DataStore.query(DeckRevisions)
        if (state?.isEditingOld) {
          deckRevision.RevisionNumber = Math.max(
            ...deckRevisions.filter((d) => d.DeckId === deck.id).map((d) => d.RevisionNumber)
          )

          const maxRevision = deckRevisions
            .filter((d) => d.DeckId === deck.id)
            .sort((a, b) => {
              return b.RevisionNumber - a.RevisionNumber
            })[0]

          const currentMax = deckRevisions
            .filter((d) => d.DeckId === deck.id && d.RevisionNumber === maxRevision.RevisionNumber && d.IsProposal)
            .sort((a, b) => b.MinorRevisionNumber - a.MinorRevisionNumber)[0]

          deckRevision.RevisionLevel = 0
          deckRevision.MinorRevisionNumber =
            typeof currentMax?.MinorRevisionNumber === 'number' ? currentMax?.MinorRevisionNumber + 1 : 0
          deckRevision.RevisionNumber = maxRevision?.RevisionNumber
        } else {
          const lastProposal = deckRevisions
            .filter((deckRevision_) => {
              return (
                deckRevision_.DeckId === deck.id &&
                deckRevision_.RevisionNumber === deckRevision.RevisionNumber &&
                deckRevision_.IsProposal
              )
            })
            .sort((a, b) => {
              return b.MinorRevisionNumber - a.MinorRevisionNumber
            })

          deckRevision.MinorRevisionNumber =
            typeof lastProposal[0]?.MinorRevisionNumber === 'number' ? lastProposal[0]?.MinorRevisionNumber + 1 : 0
        }

        const newRevision = await createRevision(deckRevision, getTransactionMetaData())

        setDeckRevision(structuredClone(newRevision))
        setDeckRevisionId(newRevision.id)
        enqueueSnackbar('Proposal created')
      }

      setDialogData({
        isOpen: false
      })
    },
    [deckRevision, getUserName, getTransactionMetaData, state?.isEditingOld]
  )

  const onArchive = useCallback(async () => {
    setIsLoading(true)

    const description = descriptionRef.current.description

    const existing = await DataStore.query(DeckRevisions, deckRevision.id)
    const deckRevisions = await DataStore.query(DeckRevisions)
    for (const revision of deckRevisions) {
      if (
        revision.DeckId === existing.DeckId &&
        existing.RevisionNumber === revision.RevisionNumber &&
        revision.MinorRevisionNumber === existing.MinorRevisionNumber &&
        ((revision.IsTrial && existing.IsTrial) || (revision.IsProposal && existing.IsProposal))
      ) {
        await updateRevision(
          revision,
          {
            ...structuredClone(revision),
            Description: revision.id === existing.id ? description : revision.Description,
            IsArchived: true,
            IsPublished: false,
            Type: `Archived ${existing.IsTrial ? 'trial' : 'proposal'}`
          },
          getTransactionMetaData()
        )
      }
    }

    const revisionToUse = deckRevisions.find((deckRevision_) => {
      return (
        deckRevision_.DeckId === deck.id &&
        deckRevision_.RevisionNumber === existing.RevisionNumber &&
        !deckRevision_.IsTrial &&
        !deckRevision_.IsProposal &&
        deckRevision_.IsPublished
      )
    })
    if (!revisionToUse) {
      enqueueSnackbar('No published revision to be used', {
        variant: 'warning'
      })
      navigate(-1)
      return
    }
    setDeckRevision(structuredClone(revisionToUse))
    setDeckRevisionId(revisionToUse.id)
    updateDeckIfSizeChange(deckRevision)
    enqueueSnackbar(`${deckRevision.IsTrial ? 'Trial' : 'Proposal'} archived`)
    setDialogData({
      isOpen: false
    })
  }, [deckRevision, deck])

  const onPublish = useCallback(
    async (type) => {
      const description = descriptionRef.current.description
      setIsLoading(true)
      const type_ = type ?? (deckRevision.IsTrial ? 'trial' : 'proposal')
      enqueueSnackbar(`Publishing ${type_}`)
      const existing = await DataStore.query(DeckRevisions, deckRevision.id)

      if (existing && !state?.isEditingOld) {
        const revisions = await DataStore.query(DeckRevisions)
        for (const revision of revisions) {
          if (
            revision.DeckId === deckRevision.DeckId &&
            existing.RevisionNumber === revision.RevisionNumber &&
            (revision.IsTrial || revision.IsProposal)
          ) {
            await updateRevision(
              revision,
              {
                ...structuredClone(revision),
                Description: revision.id === existing.id ? description : revision.Description,
                IsArchived: true,
                IsPublished: false,
                Type: `Archived ${revision.IsTrial ? 'trial' : 'proposal'}`
              },
              getTransactionMetaData()
            )
          }
        }

        await updateRevision(
          existing,
          {
            ...structuredClone(existing),
            Status: `Published ${type_}`,
            Type: `Published ${type_}`
          },
          getTransactionMetaData()
        )
      }

      deckRevision.IsPublished = true
      deckRevision.SiteId = deck.SiteId
      deckRevision.IsTrial = false
      deckRevision.IsProposal = false
      deckRevision.Description = description
      deckRevision.CreatedBy = getUserName()
      deckRevision.CustomerId = deck.CustomerId
      deckRevision.SiteId = deck.SiteId

      deckRevision.RevisionLevel = 0
      deckRevision.Type = null
      deckRevision.Status = null

      if (state?.isEditingOld) {
        const deckRevisions = await DataStore.query(DeckRevisions)

        deckRevision.RevisionNumber = Math.max(
          ...deckRevisions.filter((d) => d.DeckId === deck.id).map((d) => d.RevisionNumber)
        )

        const maxRevision = deckRevisions
          .filter((d) => d.DeckId === deck.id && d.IsPublished)
          .sort((a, b) => {
            return b.RevisionNumber - a.RevisionNumber
          })[0]

        deckRevision.RevisionNumber = (maxRevision.RevisionNumber ?? 0) + 1
      } else {
        deckRevision.RevisionNumber = new BigNumber(deckRevision.RevisionNumber ?? 0).plus(1).toNumber()
      }
      deckRevision.MinorRevisionNumber = 0

      const result = await createRevision(deckRevision, getTransactionMetaData())
      setDeckRevision(structuredClone(result))
      setDeckRevisionId(result.id)
      updateDeckIfSizeChange(deckRevision)
      enqueueSnackbar(`${type.charAt(0).toUpperCase()}${type.slice(1)} published`)
      history.clearHistory()
      setDialogData({
        isOpen: false
      })
    },
    [deckRevision, history, state?.isEditingOld]
  )

  const buttons = useMemo(() => {
    const buttons = []
    const hasDeckRevision = deckRevision && Object.entries(deckRevision).length
    const hasKits = hasDeckRevision && deckRevision.Kits && Object.entries(deckRevision.Kits).length
    if (hasDeckRevision) {
      const kitButton = hasKits ? (
        <Stack flexDirection="row" alignItems="center" key="kits">
          <CSVDownloader
            bom
            filename={`${deck.DeckHeader.DeckLevel}-${deck.DeckHeader.MaterialNumber.replace('.', '-')}`}
            data={() => kitsToExport}>
            <Button variant="outlined" color="secondary" sx={buttonStyling}>
              Export kits
            </Button>
          </CSVDownloader>
          <DeckMappToolTip title="Exports the BOM to a CSV file, that will save locally to your device" />
        </Stack>
      ) : (
        <CSVReader config={csvConfig} onUploadAccepted={(results) => importKits(results)}>
          {({ getRootProps }) => (
            <Button {...getRootProps()} variant="outlined" color="secondary" sx={buttonStyling}>
              Import kits
            </Button>
          )}
        </CSVReader>
      )
      buttons.push(kitButton)
      const hasActiveTrial =
        typeof currentMaxRevisionTrial !== 'undefined' && currentMaxRevisionTrial.IsArchived !== true
      const createTrialButton = createButton(
        'Create trial',
        () => {
          setDialogData({
            type: 'Trial',
            title: 'Create trial',
            isOpen: true,
            onSave: () => onTrial()
          })
        },
        hasActiveTrial,
        hasActiveTrial
          ? `To create a new trial, archive the current active Trial(${parsedRevisionNumberWithSubLetter(
              currentMaxRevisionTrial
            )})`
          : false
      )

      const proposalButton = createButton('Create proposal', () => {
        setDialogData({
          type: 'Proposal',
          title: 'Create proposal',
          isOpen: true,
          onSave: () => onProposal()
        })
      })

      if (deckRevision.IsTrial) {
        const publishButton = createButton('Publish trial', () => {
          setDialogData({
            type: 'Trial',
            title: 'Publish trial',
            isOpen: true,
            onSave: () => onPublish('Trial')
          })
        })

        buttons.push(
          <Fragment key="trial-buttons">
            {createButton('Update trial', () => {
              setDialogData({
                type: 'Trial',
                title: 'Update trial',
                isOpen: true,
                onSave: () => {
                  onTrial(true)
                }
              })
            })}
            {publishButton}
            {createButton('Archive trial', () => {
              setDialogData({
                type: 'Trial',
                title: 'Archive trial',
                isOpen: true,
                onSave: () => {
                  onArchive()
                }
              })
            })}
          </Fragment>
        )
      } else if (deckRevision.IsProposal) {
        buttons.push(
          <Fragment key="proposal-buttons">
            {createButton('Update proposal', () => {
              setDialogData({
                type: 'Proposal',
                title: 'Update proposal',
                isOpen: true,
                onSave: () => {
                  onProposal(true)
                }
              })
            })}
            {createTrialButton}
            {createButton('Publish proposal', () => {
              setDialogData({
                type: 'Proposal',
                title: 'Publish proposal',
                isOpen: true,
                onSave: () => onPublish('Proposal')
              })
            })}

            {deckRevision.IsArchived
              ? null
              : createButton('Archive proposal', () => {
                  setDialogData({
                    type: 'Proposal',
                    title: 'Archive proposal',
                    isOpen: true,
                    onSave: () => {
                      onArchive()
                    }
                  })
                })}
          </Fragment>
        )
      }

      if (!deckRevision.IsTrial && !deckRevision.IsProposal) {
        buttons.push(
          <Fragment key="create-buttons">
            {createTrialButton}
            {proposalButton}
            {createButton('Publish revision', () => {
              setDialogData({
                type: 'Revision',
                title: 'Publish revision',
                isOpen: true,
                onSave: () => {
                  onPublish('Revision')
                }
              })
            })}
          </Fragment>
        )
      }
    } else {
      buttons.push(
        <LoadingButton
          loading={isLoading}
          key="create-revision"
          variant="outlined"
          color="secondary"
          onClick={onNewRevision}
          sx={buttonStyling}>
          Create Revision
        </LoadingButton>
      )
    }
    return buttons
  }, [deckRevision, kitsToExport, getUserName, getTransactionMetaData, deckRevision])

  if (!hasDeck || buttons.length === 0) {
    return null
  }
  return (
    <Stack flexDirection="row" alignItems="center">
      {buttons}
      {dialogData.isOpen && (
        <DeckMappDialog
          open
          isLoading={isLoading}
          saveButtonProps={{
            onClick: dialogData.onSave,
            title: dialogData.title
          }}
          title={dialogData.title}
          onCancel={() =>
            setDialogData({
              isOpen: false
            })
          }>
          <DescriptionModal ref={descriptionRef} type={dialogData.type} />
        </DeckMappDialog>
      )}
    </Stack>
  )
}
